"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiPiece } from "@/hooks/use-projects";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { AiScoreBadge } from "@/components/ui/ai-score-badge";
import { PlatformIcon, getPlatformLabel } from "@/components/ui/platform-icon";
import { POST_TYPE_META, type PostType } from "@/hooks/use-post-generation";
import { MediaPreview, MediaLightbox, mediaItemsFromPiece } from "@/components/media";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { formatDate, useLocale } from "@/i18n/format";
import { AlertTriangle, Check, X, RotateCcw, Send, Calendar, Loader2, Image as ImageIcon } from "lucide-react";

export interface PieceDetailProps {
  piece: ApiPiece;
  onApprove?: (pieceId: string) => void;
  onReject?: (pieceId: string) => void;
  onSubmit?: (pieceId: string) => void;
  onRevise?: (pieceId: string) => void;
  onSchedule?: (pieceId: string) => void;
  canApprove: boolean;
  canEdit: boolean;
  canSchedule?: boolean;
  editingField: string | null;
  onEditField: (field: string | null) => void;
  editContent: React.ReactNode;
  /**
   * Root wrapper classes. Defaults to the slide-over's original
   * "mt-4 space-y-4" so the extraction is DOM-identical for the sole
   * existing consumer; the standalone page overrides the margin.
   */
  className?: string;
}

/**
 * Shared piece body: media preview, status row, content sections, hashtags,
 * metadata table, AI review section, and the status-action button row.
 * Extracted verbatim from PieceSlideOver so the routed piece page and the
 * review slide-over render the exact same detail UI.
 */
export function PieceDetail({
  piece, onApprove, onReject, onSubmit, onRevise, onSchedule,
  canApprove, canEdit, canSchedule, editingField, onEditField, editContent,
  className = "mt-4 space-y-4",
}: PieceDetailProps) {
  const { t } = useTranslation("pieces");
  const locale = useLocale();
  const isTranscoding = !!piece.transcodeJobId;
  const [lbIndex, setLbIndex] = useState<number | null>(null);
  const items = mediaItemsFromPiece({
    id: piece.id,
    mediaUrls: piece.mediaUrls,
    thumbnailUrl: piece.thumbnailUrl,
    format: piece.format,
    title: piece.caption,
  });

  return (
    <div className={className}>
      {items[0] ? (
        <MediaPreview
          item={items[0]}
          className="aspect-video w-full rounded-lg"
          onOpen={() => setLbIndex(0)}
          label={piece.caption || piece.shortCode}
        />
      ) : (
        <div className="flex aspect-video w-full items-center justify-center rounded-lg bg-muted">
          <ImageIcon size={24} className="text-muted-foreground" />
        </div>
      )}
      <MediaLightbox
        items={items}
        index={lbIndex}
        onIndexChange={setLbIndex}
        onClose={() => setLbIndex(null)}
      />

      <div className="flex items-center gap-2">
        <StatusBadge status={piece.status} />
        <Badge variant="outline" className="text-xs">v{piece.version}</Badge>
        {piece.requiresReapproval && (
          <div className="flex items-center gap-1 text-amber-400 text-xs">
            <AlertTriangle size={12} />
            {t("pieces:detail.requiresReapproval")}
          </div>
        )}
        {isTranscoding && (
          <div className="flex items-center gap-1 text-blue-400 text-xs">
            <Loader2 size={12} className="animate-spin" />
            {t("pieces:transcoding")}
          </div>
        )}
      </div>

      <Separator />

      <ContentSection label={t("pieces:detail.copy")} value={piece.copy} field="copy" editingField={editingField} onEditField={onEditField} canEdit={canEdit} editContent={editContent} />
      <ContentSection label={t("pieces:detail.caption")} value={piece.caption} field="caption" editingField={editingField} onEditField={onEditField} canEdit={canEdit} editContent={editContent} />

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("pieces:detail.hashtags")}</span>
          {canEdit && editingField !== "hashtags" && (
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => onEditField("hashtags")}>{t("common:actions.edit")}</Button>
          )}
        </div>
        {editingField === "hashtags" ? editContent : (
          <div className="flex flex-wrap gap-1">
            {piece.hashtags.length > 0 ? (
              piece.hashtags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs">#{tag}</Badge>
              ))
            ) : (
              <span className="text-sm text-muted-foreground">{t("pieces:detail.noHashtags")}</span>
            )}
          </div>
        )}
      </div>

      <Separator />

      <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t("pieces:detail.platform")}</span>
          <span className="flex items-center gap-1">
            <PlatformIcon platform={piece.platform} size={14} />
            {getPlatformLabel(piece.platform)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t("pieces:detail.format")}</span>
          <span>
            {piece.postType && POST_TYPE_META[piece.postType as PostType]
              ? POST_TYPE_META[piece.postType as PostType].label
              : piece.format.toLowerCase()}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t("pieces:detail.created")}</span>
          <span>{formatDate(piece.createdAt, locale)}</span>
        </div>
        {piece.approvedBy && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t("pieces:detail.approvedAt")}</span>
            <span>{piece.approvedAt ? formatDate(piece.approvedAt, locale) : "—"}</span>
          </div>
        )}
        {piece.rejectionReason && (
          <div>
            <span className="text-muted-foreground text-xs">{t("pieces:detail.rejectionReason")}</span>
            <p className="mt-1 text-sm text-red-400 bg-red-500/10 rounded-md p-2">{piece.rejectionReason}</p>
          </div>
        )}
        {piece.aiReviewScore != null && (
          <AiReviewSection score={piece.aiReviewScore} notes={piece.aiReviewNotes} />
        )}
      </div>

      <Separator />

      <div className="flex gap-2">
        {isTranscoding ? (
          <div className="flex-1 text-center text-sm text-muted-foreground py-2 flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" />
            {t("pieces:detail.waitingTranscode")}
          </div>
        ) : (
          <>
            {piece.status === "DRAFT" && canEdit && (
              <Button className="flex-1" onClick={() => onSubmit?.(piece.id)}>
                <Send size={14} className="mr-2" /> {t("pieces:detail.actions.submit")}
              </Button>
            )}
            {piece.status === "PENDING_REVIEW" && canApprove && (
              <>
                <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => onApprove?.(piece.id)}>
                  <Check size={14} className="mr-2" /> {t("pieces:actions.approve")}
                </Button>
                <Button variant="destructive" className="flex-1" onClick={() => onReject?.(piece.id)}>
                  <X size={14} className="mr-2" /> {t("pieces:actions.reject")}
                </Button>
              </>
            )}
            {piece.status === "REJECTED" && canEdit && (
              <Button variant="outline" className="flex-1" onClick={() => onRevise?.(piece.id)}>
                <RotateCcw size={14} className="mr-2" /> {t("pieces:detail.actions.revise")}
              </Button>
            )}
            {piece.status === "APPROVED" && canSchedule && (
              <Button variant="outline" className="flex-1 border-blue-500 text-blue-400 hover:bg-blue-500/10" onClick={() => onSchedule?.(piece.id)}>
                <Calendar size={14} className="mr-2" /> {t("pieces:detail.actions.schedule")}
              </Button>
            )}
            {piece.status === "SCHEDULED" && (
              <div className="flex-1 text-center text-sm text-blue-400">
                {t("pieces:status.SCHEDULED")}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AiReviewSection({
  score,
  notes,
}: {
  score: number;
  notes: ApiPiece["aiReviewNotes"];
}) {
  const { t } = useTranslation("pieces");
  // aiReviewNotes is untyped JSON from the server — guard every access so a
  // malformed payload degrades to "score only" instead of crashing the sheet.
  const n = notes as Record<string, unknown> | null | undefined;
  const issues = Array.isArray(n?.issues)
    ? (n!.issues as unknown[]).filter((i): i is string => typeof i === "string")
    : [];
  const suggestions = Array.isArray(n?.suggestions)
    ? (n!.suggestions as unknown[]).filter((s): s is string => typeof s === "string")
    : [];
  const model = typeof n?.model === "string" ? n.model : null;
  const autoApproved = n?.autoApproved === true;

  return (
    <div className="pt-2 space-y-2">
      <div className="flex items-center gap-2">
        <AiScoreBadge score={score} />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("pieces:detail.aiReview")}</span>
        {model && <span className="text-xs text-muted-foreground">{model}</span>}
        {autoApproved && (
          <Badge variant="outline" className="text-xs text-green-400 border-green-500/20">
            {t("pieces:detail.autoApproved")}
          </Badge>
        )}
      </div>
      {issues.length > 0 ? (
        <ul className="space-y-1">
          {issues.map((issue, i) => (
            <li key={i} className="flex items-start gap-1.5 text-sm text-red-400">
              <AlertTriangle size={12} className="mt-1 flex-shrink-0" />
              <span>{issue}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{t("pieces:detail.noIssues")}</p>
      )}
      {suggestions.length > 0 && (
        <ul className="space-y-1">
          {suggestions.map((suggestion, i) => (
            <li key={i} className="flex items-start gap-1.5 text-sm text-muted-foreground">
              <span className="mt-0.5 flex-shrink-0">•</span>
              <span>{suggestion}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ContentSection({
  label, value, field, editingField, onEditField, canEdit, editContent,
}: {
  label: string; value: string; field: string; editingField: string | null;
  onEditField: (f: string | null) => void; canEdit: boolean; editContent: React.ReactNode;
}) {
  const { t } = useTranslation("pieces");
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</span>
        {canEdit && editingField !== field && (
          <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => onEditField(field)}>{t("common:actions.edit")}</Button>
        )}
      </div>
      {editingField === field ? editContent : (
        <p className="text-sm whitespace-pre-wrap break-words">
          {value || <span className="text-muted-foreground">{t("pieces:detail.noField", { field: label.toLowerCase() })}</span>}
        </p>
      )}
    </div>
  );
}
